import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { ListingAnswerPublic, ListingPublic, ListingVariationPublic } from "../../../globalTypes/listings.types";
import { Logger } from "../helper/logger";
import { deleteAllImagesOnListing, DeleteAllImagesOnListingVariables, deleteListingVariation, DeleteListingVariationVariables, getListing, getListings, upsertListingAnswer, UpsertListingAnswerVariables, upsertListingCore, UpsertListingCoreVariables, upsertListingImage, UpsertListingImageVariables, upsertListingVariation, UpsertListingVariationVariables } from "@dataconnect/account";
import { filter, last, lastValueFrom, take } from "rxjs";
import { getShippingCostFromListing } from "../../../globalTypes/_helpers";
import { accountFeature } from "../store/account.store";
import { listingsFeature, ListingsPageActions } from "../store/listings.store";
import { ImageUploadPublic, TransferNewUploadedImagesVariables } from "../../../globalTypes/image.types";
import { sortByKey } from "../helper/general";
import { LOCATION_LABEL } from "../../../globalTypes/configs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { v4 as uuidV4} from 'uuid';
import { FilteredSearchVariables, SearchParams } from "../../../globalTypes/search.types";
import { httpsCallable, HttpsCallableResult } from "firebase/functions";
import { Functions } from "@angular/fire/functions";
import { searchFeature } from "../store/search.store";
import { sleep } from "../../../globalTypes/_helpers";
import { onCallFunctions } from "../../../globalTypes/backend.api.types";
import { DigitalFolderPublic, DigitalItemPublic, TransferNewUploadedFilesVariables } from "../../../globalTypes/file.types";
const log = new Logger('ListingService');
@Injectable({
  providedIn: 'root'
})
export class ListingService {

  constructor(
    private store: Store,
    private functions: Functions,
    private snackBar: MatSnackBar,
  ){}

  async createBlankListing(): Promise<Partial<ListingPublic>> {
    const location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(take(1)));
    log.log({location})
    if(!location) throw new Error("No "+LOCATION_LABEL+" found in the create blank listing function")
    return {
      id: 'new',
      locationId: location?.id || "",
      listingImages_on_listing: [],
      listingAnswers_on_listing: [],
      listingVariations_on_listing: [this.createBlankListingVariation()]
    };
  }
  createBlankListingVariation(): Partial<ListingVariationPublic> {
    return {id: uuidV4()};
  }

  async loadListings(){
    log.log('loadListings called')
    const listingRef = await getListings();
    const listings = listingRef.data.listings as unknown as ListingPublic[];
    log.log({listings: listings});
    this.store.dispatch(ListingsPageActions.listingsLoaded({listings: listings}));
  }

  async loadFilteredListings(keepExisting = false){
    const searchState = await lastValueFrom(this.store.select(searchFeature.selectSearchState).pipe(take(1)));
    const location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(take(1)));
    const { limit, offset, params} = searchState
    const cleanedParams = {...params}
    let couldBeMore = false;
    if(cleanedParams.priceMax === undefined) delete cleanedParams.priceMax;
    if(cleanedParams.priceMin === undefined) delete cleanedParams.priceMin;
    const callable = httpsCallable<FilteredSearchVariables, ListingPublic[]>(this.functions, onCallFunctions.filteredSearch);
    log.log({cleanedParams})
    const result: HttpsCallableResult<ListingPublic[]> = await callable({cleanedParams, limit, offset});
    log.log({result})
    const listings = result.data as ListingPublic[];
    const processedListings = listings.filter(listing => {
      const inStock = listing.listingVariations_on_listing.reduce((a,c)=>{ a += c.quantity || 0; return a}, 0) || 0
      return inStock > 0 || listing.category?.isDigital || (location && listing.locationId === location?.id)
    });
    log.log({listings: processedListings});
    if(!keepExisting) this.store.dispatch(ListingsPageActions.allListingsRemoved());
    if(listings && listings.length > 0){
      this.store.dispatch(ListingsPageActions.listingsLoaded({listings: processedListings}));
    }
    const target = offset + limit;
    if(listings.length >= limit){
      couldBeMore = true;
    }
    return couldBeMore;
  }

  // loadMyListings
  
  async saveListing(listing: ListingPublic | Partial<ListingPublic>){
    if(!listing) throw new Error('Listing not found');
    log.log({listing});
    if(listing?.id === "new"){
      listing.id = uuidV4();
    }
    // listing.id;
    let listingId
    try{
      listingId = await this.upsertListingCore(listing);
    } catch(error) {
      throw new Error("Error upserting listing"); 
    }
    
    const imageIdsThatNeedTransferring = listing.listingImages_on_listing?.filter(img => img.image.statusId === 'initiated' || /tempUploaded/.test(img.image.url)).map(img => img.image.id)
    if(imageIdsThatNeedTransferring && imageIdsThatNeedTransferring?.length > 0){
      const vars: TransferNewUploadedImagesVariables = {
        objectId: listingId,
        imageIds: imageIdsThatNeedTransferring,
      }
      log.log({vars})
      const callable = httpsCallable<TransferNewUploadedImagesVariables, {success: boolean}>(this.functions, 'transferNewUploadedImages');
      const result: HttpsCallableResult<{success: boolean }> = await callable(vars);
      log.log({imageIdsThatNeedTransferringResult: result})
    }
    // FIXME: This needs to go to a holding area to then be scanned by the anti-virus before being fully available
    const fileIdsThatNeedTransferring = listing.digitalFolders_on_listing?.reduce((acc, folder) => {
      const fileIds = folder.digitalItems_on_folder.reduce((ac2, file) => {
        if(file.statusId === 'initiated' || /tempUploaded/.test(file.url)){ ac2.push(file.id); }
        return ac2;
      }, [] as string[] )
      acc.push(...fileIds);
      return acc;
    }, [] as string[]);
    if(fileIdsThatNeedTransferring && fileIdsThatNeedTransferring?.length > 0){
      const vars: TransferNewUploadedFilesVariables = {
        objectId: listingId,
        digitalItemIds: fileIdsThatNeedTransferring,
      }
      log.log({vars})
      const callable = httpsCallable<TransferNewUploadedFilesVariables, {success: boolean}>(this.functions, 'transferNewUploadedFiles');
      const result: HttpsCallableResult<{success: boolean }> = await callable(vars);
      log.log({fileIdsThatNeedTransferringResult: result})
    }
    log.log({listingId})
    // [ ] Check if there are no variations, images or answers if this fails (it shouldn't)
    try {
      await this.saveDigitalInformation(listing);
    } catch(error) { throw new Error("Error saving digital item details")}
    try {
      await this.saveListingVariations(listing.listingVariations_on_listing, listing.locationId, listingId);
    } catch(error) { throw new Error("Error saving variation details")}
    try {
      const imagesInOrder = listing.listingImages_on_listing?.sort(sortByKey("sortOrder")).map(i => i.image) || []
      await this.saveListingImages(imagesInOrder, listing.locationId, listingId);
    } catch(error) { throw new Error("Error saving image details")}
    try {
      await this.saveListingAnswers(listing.listingAnswers_on_listing, listing.locationId, listingId);
    } catch(error) { throw new Error("Error saving listing category answers")}
    // update the listing from the db
    try {
      const updatedListing = await getListing({listingId});      
      this.store.dispatch(ListingsPageActions.progressListing({listing: updatedListing.data.listings[0] as ListingPublic}));
      this.store.dispatch(ListingsPageActions.listingUpdated({listingId, listing: updatedListing.data.listings[0] as ListingPublic}));
    } catch(error) {
      throw new Error("Error updating listing from the db");
    }
  }

  compileSearchContents(listing: Partial<ListingPublic>): string {
    let contents = listing.name?.toLowerCase() || "";
    if(listing.listingVariations_on_listing && listing.listingVariations_on_listing.length > 0){
      const variationNames = listing.listingVariations_on_listing.map(v => v.name?.toLowerCase() || "").join(" ");
      contents += " " + variationNames;
    }
    if(listing.listingAnswers_on_listing && listing.listingAnswers_on_listing.length > 0){
      const answerTexts = listing.listingAnswers_on_listing.map(la => {
        if(la.question?.isSearchable){
          if(la.question.questionType === "TEXT"){
            return la.answer?.answerText?.toLowerCase() || "";
          } else {
            return (la.answer?.answerValues || []).map(v => v.toLowerCase()).join(" ");
          }
        }
        return "";
      }).join(" ");
      contents += " " + answerTexts;
    }
    return contents;
  }

  async saveDigitalInformation(listing: Partial<ListingPublic>){
    const isDigital = listing.category?.isDigital || listing.digitalFolders_on_listing && listing.digitalFolders_on_listing.length > 0;
    if(!isDigital) return;
    const listingId = listing.id;
    const locationId = listing.locationId;
    if(!listingId) throw new Error('Listing Id for digital information not found');
    if(!locationId) throw new Error('Location Id for digital information not found');
    // save folders then update linkage
    try {
      const callable = httpsCallable<{listing: Partial<ListingPublic>}, {success: boolean}>(this.functions, onCallFunctions.saveDigitalInformation);
      await callable({listing});
    } catch (error: any){
      throw new Error("Error saving digital information: " + error.message || "Unknown error");
    }
  }

  async saveListingVariations(variations?: Partial<ListingVariationPublic>[], locationId?:string, listingId?: string){
    if(!variations) throw new Error('Variations not found');
    if(!locationId) throw new Error('Location Id for variation not found');
    for (let index = 0; index < variations.length; index++) {
      const variation = variations[index];
      await this.upsertListingVariation(variation, locationId, listingId);
      // if(!variation.id){
      //   await this.insertListingVariation(variation, locationId, listingId);
      // } else {
      //   await this.upsertListingVariation(variation, locationId);
      // }
    }
  }

  async saveListingImages(images?: Partial<ImageUploadPublic>[], locationId?:string, listingId?: string){
    log.log({images})
    if(!images) throw new Error('Images not found');
    if(!locationId) throw new Error('Location Id for image not found');
    // Remove all images from listing
    await this.removeListingImages(locationId, listingId)
    // Add back current images
    for (let index = 0; index < images.length; index++) {
      const image = images[index];
      log.log(image, index, locationId, listingId)
      await this.upsertListingImage(image, index, locationId, listingId);
      
    }
  }

  async saveListingAnswers(answers?: Partial<ListingAnswerPublic>[], locationId?:string, listingId?: string){
    if(!answers) throw new Error('Answers not found');
    if(!locationId) throw new Error('Location Id for answer not found');
    log.log({answers, locationId})
    for (let index = 0; index < answers.length; index++) {
      const answer = answers[index];
      await this.upsertListingAnswer(answer, locationId, listingId)
    
    }

  }

  async upsertListingAnswer(answer: Partial<ListingAnswerPublic>, locationId?: string, listingId?: string){
    if(!answer || !answer.answer) throw new Error('Answer not found');
    if(!listingId) throw new Error('Listing Id for answer not found'); 
    try {
      const vars: UpsertListingAnswerVariables = {
        questionId: answer.question?.id || "",
        answerId: answer.answer?.id || uuidV4(),
        answerText: answer.answer?.answerText || "",
        answerValues: answer.answer?.answerValues || [],
        locationId: locationId || "",
        listingId: listingId,
      }
      log.log({vars})
      await upsertListingAnswer(vars);
    } catch (error: any){
      throw new Error("Error saving answer: " + error.message || "Unknown error");
    }
  }
  async removeListingImages(locationId?: string, listingId?: string){
    if(!listingId) throw new Error('Listing Id for image not found');
    try {
      const vars: DeleteAllImagesOnListingVariables = {
        locationId: locationId || "",
        listingId: listingId
      }
      await deleteAllImagesOnListing(vars);
    } catch (error: any){
      throw new Error("Error saving image: " + error.message || "Unknown error");
    }
  }
  async upsertListingImage(image: Partial<ImageUploadPublic>, sortOrder: number, locationId?: string, listingId?: string){
    if(!image) throw new Error('Image not found');
    if(!listingId) throw new Error('Listing Id for image not found');
    try {
      const vars: UpsertListingImageVariables = {
        imageId: image.id,
        locationId: locationId || "",
        listingId: listingId,
        sortOrder: sortOrder || 0,
      }
      await upsertListingImage(vars);
    } catch (error: any){
      throw new Error("Error saving image: " + error.message || "Unknown error");
    }
  }


  async upsertListingVariation(variation: ListingVariationPublic | Partial<ListingVariationPublic>, locationId?: string, listingId?: string){
    if(!variation) throw new Error('Variation not found');
    if(!listingId) throw new Error('Listing Id for variation not found');
    if(!locationId) throw new Error('Location Id for variation not found');
    try {
      const vars: UpsertListingVariationVariables = {
        listingVariationId: variation.id || uuidV4(),
        locationId: locationId || "",
        listingId: listingId,
        name: variation.name || "",
        description: variation.description || "",
        priceInCents: variation.priceInCents || 0,
        imageId: variation.image?.id,
        quantity: variation.quantity || 0,
        statusId: variation.statusId || "initiated",
      }
      log.log({vars});
      await upsertListingVariation(vars);
    } catch (error: any){
      log.error(error);
      throw new Error("Error saving listing: " + error.message || "Unknown error");
    }
  }
  async deleteListingVariation(variationId: string, locationId?: string, listingId?: string){
    if(!variationId) throw new Error('Variation Id not found');
    if(!listingId) throw new Error('Listing Id for variation not found');
    if(!locationId) throw new Error('Location Id for variation not found');
    try {
      const vars: DeleteListingVariationVariables = {
        listingVariationId: variationId || uuidV4(),
        locationId: locationId || "",
        listingId: listingId,
      }
      log.log({vars});
      await deleteListingVariation(vars);
    } catch (error: any){
      log.error(error);
      throw new Error("Error deleting listing variation: " + error.message || "Unknown error");
    }
  }

  async upsertListingCore(listing: ListingPublic | Partial<ListingPublic>): Promise<string> {
    if(!listing) throw new Error('Listing not found')
    try {
      const listingId = listing.id || uuidV4();
      const location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(take(1)))
      const {height, width, length, weight, distanceUnit, weightUnit, shippingCompressionRate} = listing;
      const vars: UpsertListingCoreVariables = {
        listingId,
        name: listing.name || "",
        description: listing.description,
        categoryId: listing.categoryId || 0,
        searchContents: listing.searchContents || "",
        lowestPrice: listing.lowestPrice || 0,
        shippingCost: listing.shippingCost || 0,
        shippingAdditionalCost: listing.shippingAdditionalCost || 0,
        shippingType: listing.shippingType || "",
        distanceUnit: distanceUnit || 'ft',
        shippingCompressionRate: shippingCompressionRate || 0,
        height: height || 0,
        width: width || 0,
        length: length || 0,
        weight: weight || 0,
        weightUnit: weightUnit || "lb",
        locationId: location?.id || "",
        statusId: listing.statusId || "initiated",
      }
      await sleep(1000);
      console.log({vars})
      const rtn = await upsertListingCore(vars);
      return rtn.data.listing.id;
    } catch (error: any){
      log.error(error);
      throw new Error("Error updating listing: " + error.message || "Unknown error");
    }
  }

  getShippingCostText(listing: ListingPublic | Partial<ListingPublic>, excludeAdditionalCostText: boolean = false): string | null {
    const costs = getShippingCostFromListing(listing);
    if(!costs || costs.shippingCost === null) return null;
    const { shippingCost, shippingAdditionalCost } = costs;
    if(listing.shippingType === 'Digital Download') return `Digital Download`;
    const shippingCostText = shippingCost && shippingCost > 0 ? `$${(shippingCost).toFixed(2)}` : 'Free';
    const shippingAdditionalCostText = !excludeAdditionalCostText && shippingAdditionalCost && shippingAdditionalCost > 0 && shippingAdditionalCost !== shippingCost ? ` + $${(shippingAdditionalCost).toFixed(2)} per additional item` : '';
    return `${shippingCostText} Shipping ${shippingAdditionalCostText}`;
  }   
}